import {Component, computed, effect, inject, output, signal} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';
import {HttpClient} from '@angular/common/http';
import {CommentContextService} from '../../services/comment-context.service';
import {DialogService} from '../../services/dialog.service';
import {SnackBarService} from '../../services/snackbar.service';
import {DynamicConfirmationDialogComponent} from '../dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {CustomActionCollectionHal, CustomActionHal} from '../../models/HAL/custom-action-hal.model';

@Component({
  selector: 'app-case-actions',
  imports: [MatButton, MatIconButton, MatIcon, TranslateModule],
  templateUrl: './case-actions.component.html',
  styleUrl: './case-actions.component.scss',
})
export class CaseActionsComponent {
  closed = output<void>();

  actions = signal<CustomActionHal[]>([]);
  loading = signal(false);
  executing = signal(false);

  private readonly httpClient = inject(HttpClient);
  private readonly commentContextService = inject(CommentContextService);
  private readonly dialogService = inject(DialogService);
  private readonly snackBarService = inject(SnackBarService);

  protected readonly getCustomActionsLink = computed(() => this.commentContextService.panelHal()?._links.getLink('getCustomActions')?.href);

  constructor() {
    effect(() => {
      const link = this.getCustomActionsLink();
      if (link) {
        this.loadActions(link);
      } else {
        this.actions.set([]);
      }
    });
  }

  onExecute(action: CustomActionHal) {
    const executeLink = action._links.getLink('execute')?.href;
    if (!executeLink) {
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseActions.confirmDialog.title',
        titleParams: {name: action.name},
        message: 'caseActions.confirmDialog.message',
        confirmButtonText: 'caseActions.confirmDialog.confirm',
        cancelButtonText: 'caseActions.confirmDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.executeAction(executeLink, action.name);
      }
    });
  }

  close() {
    this.closed.emit();
  }

  private executeAction(executeLink: string, actionName: string) {
    this.executing.set(true);
    this.httpClient.post(executeLink, {}).subscribe({
      next: () => {
        this.executing.set(false);
        this.snackBarService.open('caseActions.executeSuccess', 3, '', {name: actionName});
        this.commentContextService.requestCaseRefresh();
      },
      error: () => {
        this.executing.set(false);
      }
    });
  }

  private loadActions(link: string) {
    this.loading.set(true);
    this.httpClient.get<any>(link).subscribe({
      next: (response) => {
        const collection = new CustomActionCollectionHal(response);
        this.actions.set(collection._embedded?.customActions ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.snackBarService.open('caseActions.loadError', 3);
      }
    });
  }
}
